import type { AssessmentStatus } from "@/types";
import { useTranslation } from "@/hooks/useTranslation";

// ─────────────────────────────────────────────
// VersionSelector
//
// Dropdown to switch between assessment versions.
// Historical versions are read-only.
// Design: §6.2.2 Entity Detail — Version Selector
// ─────────────────────────────────────────────

export interface VersionOption {
  id: string;
  version: number;
  status: AssessmentStatus;
  createdAt: string;
}

interface VersionSelectorProps {
  versions: VersionOption[];
  currentId: string;
  onSelect: (id: string) => void;
  disabled?: boolean;
}

// Key paths only (not user-visible text) — safe to keep at module scope,
// reuses the existing status.* translation keys.
const STATUS_LABEL_KEYS: Record<AssessmentStatus, string> = {
  DRAFT: "status.draft",
  SUBMITTED: "status.submitted",
  IN_REVIEW: "status.inReview",
  APPROVED: "status.approved",
  REJECTED: "status.rejected",
};

function statusLabel(status: AssessmentStatus, t: (key: string) => string): string {
  return t(STATUS_LABEL_KEYS[status]);
}

export function VersionSelector({
  versions,
  currentId,
  onSelect,
  disabled,
}: VersionSelectorProps) {
  const { t } = useTranslation();
  if (versions.length === 0) return null;

  return (
    <div className="flex items-center gap-2">
      <span className="text-[13px] font-medium text-text-muted">{t("assessment.version")}:</span>
      <select
        value={currentId}
        onChange={(e) => onSelect(e.target.value)}
        disabled={disabled}
        className="h-8 rounded-control border border-border-default bg-bg-input px-2 text-[13px] text-text-primary focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent disabled:opacity-50"
      >
        {[...versions].reverse().map((v) => (
          <option key={v.id} value={v.id}>
            v{v.version} ({statusLabel(v.status, t)})
          </option>
        ))}
      </select>
    </div>
  );
}
